export const REPAIR_VERIFY_WARNING = "Repair write completed, but verification failed. Please re-run diagnose.";
export const ACTIVITY_SORT_WARNING = "You are updating the activity date used for sorting. This may affect where the record appears in Saved Quotes, CRM, or Orders.";
export const NO_ACTIVITY_HISTORY_WARNING = "No historical activity date found. This record should not be auto-repaired.";
export const ACTIVITY_REPAIR_SOURCES = ["updatedAt", "createdAt", "quoteDate", "date"];

export function hasActivityDateValue(value) {
  return value != null && !(typeof value === "string" && value.trim() === "");
}

export function activityDateMillis(value) {
  if (!hasActivityDateValue(value) || typeof value === "boolean" || value?._methodName) return null;
  try {
    const millis = value?.toMillis ? value.toMillis() : value?.toDate ? value.toDate().getTime()
      : value instanceof Date ? value.getTime()
      : typeof value === "object" && (value.seconds != null || value._seconds != null)
      ? Number(value.seconds ?? value._seconds) * 1000 + Number(value.nanoseconds ?? value._nanoseconds ?? 0) / 1e6
      : new Date(value).getTime();
    return Number.isFinite(millis) && Number.isFinite(new Date(millis).getTime()) ? millis : null;
  } catch (error) { return null; }
}

export function buildActivityDateProposal(record = {}) {
  if (hasActivityDateValue(record.lastActivityAt)) return null;
  for (const source of ACTIVITY_REPAIR_SOURCES) {
    const millis = activityDateMillis(record[source]);
    if (millis == null) continue;
    return { currentLastActivityAt: record.lastActivityAt ?? null, proposedLastActivityAt: record[source], proposedLastActivityAtMs: millis, source, risk: "Safe" };
  }
  return { currentLastActivityAt: record.lastActivityAt ?? null, proposedLastActivityAt: null, proposedLastActivityAtMs: null, source: "none", risk: "Manual Only", warning: NO_ACTIVITY_HISTORY_WARNING };
}

export function canRepairActivityDateRow(row = {}) {
  return !row.repaired && row.risk !== "Manual Only" && !hasActivityDateValue(row.currentLastActivityAt)
    && ACTIVITY_REPAIR_SOURCES.includes(row.source) && activityDateMillis(row.proposedLastActivityAt) != null;
}

export function assertFreshActivityDateProposal(record, preview) {
  if (!canRepairActivityDateRow(preview)) throw new Error(NO_ACTIVITY_HISTORY_WARNING);
  if (hasActivityDateValue(record.lastActivityAt)) throw new Error("This record already has an activity date. Run a new dry run.");
  const fresh = buildActivityDateProposal(record);
  if (!canRepairActivityDateRow(fresh) || fresh.source !== preview.source || fresh.proposedLastActivityAtMs !== activityDateMillis(preview.proposedLastActivityAt)) {
    throw new Error("The historical activity date changed after the preview. Run a new dry run.");
  }
  return fresh;
}

export const REPAIR_SECTIONS = [
  ["links", "Quote/Customer Link Repairs", "Connect a quotation to the correct customer.", "Needs Review", "unlinkedResults", "repairQuoteCustomerLink"],
  ["quote-index", "Quote Index / Saved Quotes Repairs", "Saved Quote card does not match the actual quotation.", "Low Risk", "quoteIndexMismatchResults", "repairQuoteIndexFromFullQuote"],
  ["duplicate-deals", "Duplicate CRM Deal Repairs", "Review and archive duplicate sales records.", "Manual Only", "duplicateDealResults", "archiveDuplicateQuoteDeals"],
  ["projects", "Project / Order Pipeline Repairs", "Put an order in its correct operations stage.", "Needs Review", "projectStageResults", "repairProjectPipelineStage"],
  ["duplicate-projects", "Duplicate Project Repairs", "Review duplicate orders while preserving their history.", "Manual Only", "duplicateProjectResults", "archiveDuplicateProjects"],
  ["customer-search", "Customer Search Index Repairs", "Refresh search information for one selected customer.", "Low Risk", "customerIndexRepairResult", "rebuildCustomerSearchIndexForCustomer"],
  ["activity", "Activity Date Repairs", "Restore missing activity dates using historical dates.", "Low Risk", "activityResults", "repairMissingActivityDates"],
  ["logs", "Repair Logs", "Read the permanent history of repair actions.", "Low Risk", "repairLogChecks", ""],
];

export function confirmGuidedRepair(records, action) {
  const selected = Array.isArray(records) ? records : [records];
  const isTest = selected.length > 0 && selected.every((record) => record?.repair_test_data === true);
  const label = isTest ? "TEST DATA" : "REAL DATA";
  const warning = isTest ? "" : "You are repairing real business data. Review carefully. This action will be logged.\n\n";
  return typeof window !== "undefined" && window.confirm(
    `${label}\n\n${warning}${action}\n\nYou are about to repair selected records. This will update only derived/index/CRM fields. It will not change quote line items or totals.`
  );
}

function comparable(value) {
  if (value?.toMillis) return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (Array.isArray(value)) return value.map(comparable);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.keys(value).sort().map((key) => [key, comparable(value[key])]));
  }
  return value;
}

export function repairFieldsMatch(actual, expected) {
  return Object.entries(expected).every(([key, value]) => {
    // Server transforms cannot be compared with their resolved timestamp values.
    if (value?._methodName || value === "serverTimestamp") return actual[key] != null;
    if (key === "updatedAt" || key.endsWith("RepairedAt")) return value == null ? actual[key] == null : actual[key] != null;
    return JSON.stringify(comparable(actual[key])) === JSON.stringify(comparable(value));
  });
}

export async function verifyRepairTargets(log, read) {
  const after = log.after || {};
  const targets = [];
  const add = (collection, id, expected) => targets.push({ collection, id, expected });
  switch (log.toolName) {
    case "archiveDuplicateQuoteDeals":
    case "archiveDuplicateProjects": {
      const deals = log.toolName === "archiveDuplicateQuoteDeals";
      const collection = deals ? "crm_leads" : "projects";
      const primaryId = deals ? after.primaryDealId : after.primaryProjectId;
      const ids = (deals ? after.duplicateDealIds : after.duplicateProjectIds) || [];
      const primary = await read(collection, primaryId);
      if (!primary || primary.archived === true || primary.deleted === true || primary.status === "duplicate_archived") throw new Error(REPAIR_VERIFY_WARNING);
      ids.forEach((id) => add(collection, id, {
        archived: true,
        status: "duplicate_archived",
        [deals ? "duplicateOfDealId" : "duplicateOfProjectId"]: primaryId,
      }));
      break;
    }
    case "repairQuoteCustomerLink":
      add("quote_index", after.quoteIndexId || log.recordId, { customerId: after.customerId });
      if (after.mode === "link_and_update_quote_customer") add("quotations", log.quoteId, { customerId: after.customerId, customerName: after.customerName });
      (after.dealIds || []).forEach((id) => add("crm_leads", id, { customerId: after.customerId }));
      if (after.customerSearchIndexId) add("customer_search_index", after.customerSearchIndexId, { latestQuoteId: log.quoteId });
      break;
    case "repairMissingActivityDates":
      add(log.recordType, log.recordId, { lastActivityAt: after.lastActivityAt });
      break;
    case "repairProjectPipelineStage":
      add("projects", log.recordId, after);
      break;
    case "repairQuoteIndexFromFullQuote":
      add("quote_index", log.recordId, after.patch || {});
      break;
    case "repairCrmDealQuoteFieldsFromQuote":
      add("crm_leads", log.recordId, after);
      break;
    case "rebuildCustomerSearchIndexForCustomer": {
      const actual = await read("customer_search_index", log.recordId);
      if (!actual ||
        (actual.displayName || actual.customerName || "") !== after.displayName ||
        (actual.nameNormalized || actual.normalizedName || "") !== after.nameNormalized ||
        (actual.phoneNormalized || actual.normalizedPhone || "") !== after.phoneNormalized ||
        (actual.emailCanonical || actual.normalizedEmail || "") !== after.emailCanonical ||
        (actual.active !== false) !== after.active || (actual.deleted === true) !== after.deleted || (actual.archived === true) !== after.archived ||
        (actual.searchTokens || actual.searchKeywords || []).length !== after.searchTokenCount ||
        (actual.searchPrefixes || []).length !== after.searchPrefixCount) throw new Error(REPAIR_VERIFY_WARNING);
      return true;
    }
    default: throw new Error(REPAIR_VERIFY_WARNING);
  }
  for (const target of targets) {
    if (!target.id) throw new Error(REPAIR_VERIFY_WARNING);
    const actual = await read(target.collection, target.id);
    if (!actual || !repairFieldsMatch(actual, target.expected)) throw new Error(REPAIR_VERIFY_WARNING);
  }
  return true;
}

export function duplicateRepairFixtures(kind, workspaceUid, actorUid, timestamp) {
  const deal = kind === "deal";
  const customerId = deal ? "repair_duplicate_deal_customer" : "repair_duplicate_project_customer";
  const quoteId = deal ? "000_TEST_DUP_DEAL_001" : "000_TEST_DUP_PROJECT_001";
  const quoteNo = deal ? "TEST-DUP-DEAL-001" : "TEST-DUP-PROJECT-001";
  const customerName = deal ? "Duplicate Deal Test Customer" : "Duplicate Project Test Customer";
  const collection = deal ? "crm_leads" : "projects";
  const primaryId = deal ? "repair_dup_deal_primary_001" : "repair_dup_project_primary_001";
  const duplicateId = deal ? "repair_dup_deal_duplicate_001" : "repair_dup_project_duplicate_001";
  const shared = { workspaceUid, repair_test_data: true, tags: ["repair_test_data", "TEST DATA - SAFE TO DELETE"], createdAt: timestamp, updatedAt: timestamp };
  const record = { ...shared, quoteId, quoteNo, customerName, ownerUid: actorUid, status: "active", archived: false, deleted: false, title: `${customerName} - TEST DATA - SAFE TO DELETE` };
  return [
    { collection: "customers", id: customerId, data: { ...shared, customerId, name: customerName, customerName, phone: deal ? "9999990004" : "9999990005", active: true } },
    { collection: "quote_index", id: quoteId, data: { ...shared, quoteId, quoteNo, customerId, customerName, deleted: false, quoteType: "general", quotationType: "general", totalAmount: 0, grandTotal: 0, [deal ? "crmLeadId" : "projectId"]: primaryId } },
    { collection, id: primaryId, data: { ...record, customerId, lastActivityAt: timestamp, ...(deal ? { stage: "quotation_sent" } : { stage: "order_confirmed", projectStage: "order_confirmed", pipelineId: "orders", orderValue: 0 }) } },
    { collection, id: duplicateId, data: { ...record, ...(deal ? {} : { customerId, orderValue: 0 }) } },
  ];
}
